'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import OrientationSidebar from './OrientationSidebar';
import OrientationLessonContent from './OrientationLessonContent';
import { ORIENTATION_LESSONS } from './orientationData';
import { trackLessonStarted, trackLessonCompleted } from '@/lib/gaEvents';

const LESSON_IDS = ORIENTATION_LESSONS.map((l) => l.id);

export default function OrientationLayout({ initialLesson }: { initialLesson?: string } = {}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const lessonParam = searchParams?.get('lesson');

  const [activeId, setActiveId] = useState<string>(
    lessonParam && LESSON_IDS.includes(lessonParam)
      ? lessonParam
      : initialLesson && LESSON_IDS.includes(initialLesson)
      ? initialLesson
      : 'o1'
  );
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // ─── Load user + orientation progress ────────────────────────────────────
  useEffect(() => {
    const supabase = createClient();
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        router.push('/login');
        return;
      }
      setUserId(user.id);

      const { data } = await supabase
        .from('lesson_progress')
        .select('lesson_id')
        .eq('user_id', user.id)
        .eq('module_id', 'orientation');

      if (data) {
        setCompletedIds(new Set(data.map((r: { lesson_id: string }) => r.lesson_id)));
      }
    };
    load();
  }, [router]);

  // ─── Sync lesson param ────────────────────────────────────────────────────
  useEffect(() => {
    if (lessonParam && LESSON_IDS.includes(lessonParam)) {
      setActiveId(lessonParam);
    }
  }, [lessonParam]);

  // ─── Mark lesson complete ─────────────────────────────────────────────────
  const markComplete = useCallback(async (id: string) => {
    setCompletedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    const lesson = ORIENTATION_LESSONS.find((l) => l.id === id);
    trackLessonCompleted({
      lesson_id: id,
      module_id: 'orientation',
      lesson_title: lesson?.title,
    });
    if (!userId) return;
    const supabase = createClient();
    await supabase.from('lesson_progress').upsert(
      { user_id: userId, lesson_id: id, module_id: 'orientation' },
      { onConflict: 'user_id,lesson_id' }
    );
  }, [userId]);

  // ─── Next lesson handler ──────────────────────────────────────────────────
  const handleNext = useCallback(async (currentId: string) => {
    await markComplete(currentId);
    const idx = LESSON_IDS.indexOf(currentId);
    if (idx < LESSON_IDS.length - 1) {
      setActiveId(LESSON_IDS[idx + 1]);
    } else {
      // O4 complete — go to Module 1
      router.push('/module/1');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [markComplete, router]);

  const handleLessonClick = (id: string) => {
    setActiveId(id);
    setSidebarOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentLesson = ORIENTATION_LESSONS.find((l) => l.id === activeId);
  const currentIdx = LESSON_IDS.indexOf(activeId);
  const isLastLesson = currentIdx === LESSON_IDS.length - 1;

  // Fire lesson_started whenever the active lesson changes
  useEffect(() => {
    const lesson = ORIENTATION_LESSONS.find((l) => l.id === activeId);
    if (lesson) {
      trackLessonStarted({
        lesson_id: lesson.id,
        module_id: 'orientation',
        lesson_title: lesson.title,
      });
    }
  }, [activeId]);

  const nextLabel = currentLesson?.nextLabel
    ? currentLesson.nextLabel
    : isLastLesson
    ? 'Start Module 1: Copilot at Work →'
    : `Next: ${ORIENTATION_LESSONS[currentIdx + 1]?.title ?? ''}`;

  return (
    <div className="flex min-h-[calc(100vh-64px)]" style={{ backgroundColor: '#F9F7F4' }}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <OrientationSidebar
        activeId={activeId}
        completedIds={completedIds}
        onLessonClick={handleLessonClick}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main content */}
      <main className="flex-1 min-w-0" style={{ backgroundColor: '#F9F7F4' }}>
        {/* Mobile header */}
        <div
          className="lg:hidden flex items-center gap-3 px-4 py-3 border-b sticky top-16 z-20"
          style={{ backgroundColor: '#F9F7F4', borderColor: '#E5E0D8' }}
        >
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg font-body text-sm font-medium flex items-center gap-2 transition-colors duration-150"
            style={{ color: '#1B2A4A' }}
            aria-label="Open lesson navigation"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <rect x="2" y="4" width="14" height="1.5" rx="0.75" fill="currentColor"/>
              <rect x="2" y="8.25" width="10" height="1.5" rx="0.75" fill="currentColor"/>
              <rect x="2" y="12.5" width="12" height="1.5" rx="0.75" fill="currentColor"/>
            </svg>
            Lessons
          </button>
          {currentLesson && (
            <span className="font-body text-sm truncate" style={{ color: '#9CA3AF' }}>
              {currentLesson.title}
            </span>
          )}
        </div>

        {currentLesson && (
          <OrientationLessonContent
            lessonNumber={currentLesson.number}
            lessonTitle={currentLesson.title}
            lessonSlug={currentLesson.slug}
            blocks={currentLesson.blocks}
            isLastLesson={isLastLesson}
            onNext={() => handleNext(currentLesson.id)}
            nextLabel={nextLabel}
          />
        )}
      </main>
    </div>
  );
}
